import { useEffect, useState } from "react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Module-level cache of the last-resolved entitlement payload. Updated by the
// hook on every successful fetch. Synchronous consumers (e.g. pdfExport.js)
// can read it via getCachedEntitlement(). When no fetch has resolved yet,
// callers receive a "preview" shape so any guard that uses this cache fails
// closed.
let _cached = {
  authenticated: false,
  tier: "free_beta",
  cadence: null,
  status: null,
  polishUnlocked: false,
  enforceProfessional: false,
  loading: true,
  loadError: false,
};

export function getCachedEntitlement() {
  return _cached;
}

// Test/diagnostic-only helper: lets internal tooling (e.g. /diagnostic) seed
// the entitlement cache so storage roundtrip tests can exercise the gated
// .save()/.remove() paths without bypassing production gates. NEVER call
// this from product UI — it has no server-side effect, only the local cache.
export function __setCachedEntitlementForTest(payload) {
  _cached = { ..._cached, ...payload };
  return _cached;
}

/**
 * Lightweight client for /api/billing/entitlement. The session JWT lives in
 * an HttpOnly cookie set by the backend; this hook never sees the token, only
 * the user's tier + polish-unlock flag.
 *
 * Returned shape includes a `loadError` flag set to true when the entitlement
 * fetch fails (network error, HTTP error, or malformed JSON) so the UI can
 * render a degraded "Unable to load" state for signed-in users whose data
 * temporarily fails to fetch.
 */
export function useEntitlement() {
  const [data, setData] = useState(_cached);
  useEffect(() => {
    let cancelled = false;
    if (!API_BASE) {
      const next = { ..._cached, loading: false, loadError: true };
      _cached = next;
      setData(next);
      return;
    }
    fetch(`${API_BASE}/api/billing/entitlement`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((j) => {
        if (cancelled || !j) return;
        const next = { ...j, loading: false, loadError: false };
        _cached = next;
        setData(next);
      })
      .catch(() => {
        if (cancelled) return;
        const next = { ..._cached, loading: false, loadError: true };
        _cached = next;
        setData(next);
      });
    return () => { cancelled = true; };
  }, []);
  return data;
}

